import React, { Component, Fragment, type ReactNode } from 'react';
import { PerformanceMark } from '@atlassian/jira-common-performance';
import { DevOpsAppRecommendationContextProvider as DevOpsAppRecommendationProvider } from '@atlassian/jira-dev-ops-app-recommendations-context-provider';
import { ff } from '@atlassian/jira-feature-flagging';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { toForgeKeyWithDate } from '@atlassian/jira-forge-ui-utils/src/utils/connect';
import AttachmentsView from '@atlassian/jira-issue-view-base/src/content/attachment/panel';
import ChildrenIssuesView from '@atlassian/jira-issue-view-base/src/content/children-issues';
import ConfluenceContentView from '@atlassian/jira-issue-view-base/src/content/confluence-content';
import * as PerfMarks from '@atlassian/jira-issue-view-base/src/content/constants';
import { DesignPanel } from '@atlassian/jira-issue-view-base/src/content/designs';
import { AsyncDevOpsAppRecommendationPanel } from '@atlassian/jira-issue-view-base/src/content/dev-ops-recommendations';
import EpicIssuesView from '@atlassian/jira-issue-view-base/src/content/epic-issues';
import { IncidentInvestigationPanel } from '@atlassian/jira-issue-view-base/src/content/incident-investigation-panel';
import IssueLinksView from '@atlassian/jira-issue-view-base/src/content/issue-links';
import LinkedAlertsModals from '@atlassian/jira-issue-view-base/src/content/linked-alerts-modals';
import ObjectRelatedRequestsPanel from '@atlassian/jira-issue-view-base/src/content/object-related-requests-panel';
import PortfolioChildIssuesView from '@atlassian/jira-issue-view-base/src/content/portfolio-child-issues';
import WrappedProformaFormsPanelView from '@atlassian/jira-issue-view-base/src/content/proforma-forms';
import SubtasksView from '@atlassian/jira-issue-view-base/src/content/subtasks';
import type { Props } from '@atlassian/jira-issue-view-base/src/content/types';
import { ConnectedView as WebLinksConnectedView } from '@atlassian/jira-issue-view-base/src/content/web-links';
import { withGICHook } from '@atlassian/jira-issue-view-base/src/content/with-gic-hook';
import { FORGE_ENTITY_TYPE } from '@atlassian/jira-issue-view-common-constants';
import { ContentItemsWrapper, IssueSectionWrapper } from '@atlassian/jira-issue-view-common-styles';
import type { Panel as ForgePanel } from '@atlassian/jira-issue-view-common-types/src/forge-types';
import type { State } from '@atlassian/jira-issue-view-common-types/src/issue-type';
import { isDevOpsAppRecommenderEnabled } from '@atlassian/jira-issue-view-common/src/feature-flags';
import ConnectContentPanel, {
	type ContentPanel,
} from '@atlassian/jira-issue-view-ecosystem/src/connect/content-panel-view';
import ForgeContentPanel from '@atlassian/jira-issue-view-ecosystem/src/forge/content-panel-view';
import { ContentTemplateRenderer } from '@atlassian/jira-issue-view-layout-templates';
import { connect } from '@atlassian/jira-issue-view-react-redux';
import {
	contextSelector,
	isAnonymousSelector,
	issueKeySelector,
} from '@atlassian/jira-issue-view-store/src/common/state/selectors/context-selector';
import { hasIssueLinksSelector as hasIssueLinksFieldSelector } from '@atlassian/jira-issue-view-store/src/common/state/selectors/field-selector';
import {
	isCompletedLoadingSelector,
	isEpicSelector,
	isServiceDeskSelector,
	projectTypeSelector,
} from '@atlassian/jira-issue-view-store/src/common/state/selectors/issue-selector';
import { ecosystemAddedContentPanelsSelector } from '@atlassian/jira-issue-view-store/src/ecosystem/ecosystem-extensions-selector';
import {
	shouldRenderChildPanelSelector,
	shouldRenderPortfolioChildPanelSelector,
	shouldRenderSubtaskPanelSelector,
} from '@atlassian/jira-issue-view-store/src/selectors/child-panel-ui-selector';
import { isIssueLinksEnabledSelector } from '@atlassian/jira-issue-view-store/src/selectors/issue-links-selector';
import { additionalWebLinksSectionsSelector } from '@atlassian/jira-issue-view-store/src/selectors/web-links-selector';
import Placeholder from '@atlassian/jira-placeholder';
import { FireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { getApplicationForProject, getEdition } from '@atlassian/jira-shared-types';
import SmartRelatedIssuesPanel from '@atlassian/jira-smart-related-issues-panel';
import UFOSegment from '@atlassian/jira-ufo-segment';
import { EcosystemIssueViewErrorBoundary } from './ecosystem-issue-view-error-boundary';

const DevOpsAppRecommendationContextProvider = componentWithCondition(
	isDevOpsAppRecommenderEnabled,
	DevOpsAppRecommendationProvider,
	({ children }: { children: ReactNode }) => <>{children}</>,
);

// TODO Decomp BENTO-12515 - convert to functional component
// eslint-disable-next-line jira/react/no-class-components
export class ContentItems extends Component<Props> {
	static displayName = 'ContentItems';

	static defaultProps = {
		isComplete: false,
		isCompactMode: false,
		isAnonymousUser: false,
		isEpic: false,
		hasIssueLinksEntity: false,
		hasIssueLinksField: false,
		contentPanels: [],
		additionalWebLinksSections: [],
	};

	maybeWrapInSectionWrapper(component: ReactNode) {
		const { isCompactMode } = this.props;

		if (!component) {
			return component;
		}

		return <IssueSectionWrapper isCompactMode={isCompactMode}>{component}</IssueSectionWrapper>;
	}

	renderEcosystemContentPanels() {
		const { contentPanels, isCompactMode } = this.props;

		const panels = contentPanels.map((panel: ForgePanel | ContentPanel) => {
			if (panel.type === FORGE_ENTITY_TYPE) {
				const { name, appKey, moduleKey, dateAdded, panelInstanceId, extension } =
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
					panel as any;

				const panelKey = toForgeKeyWithDate(appKey, moduleKey, panelInstanceId, dateAdded);

				return (
					<ForgeContentPanel
						key={panelKey}
						panelKey={panelKey}
						name={name}
						appKey={appKey}
						moduleKey={moduleKey}
						extension={extension}
						isCompactMode={isCompactMode}
						externalId="issue.ecosystem.content-panel.forge"
					/>
				);
			}

			return (
				<ConnectContentPanel
					key={panel.name}
					name={panel.name}
					appKey={panel.appKey}
					moduleKey={panel.moduleKey}
				/>
			);
		});

		return <>{panels}</>;
	}

	renderEcosystemContentPanelsNew() {
		const { contentPanels, isCompactMode, projectType, context } = this.props;
		let application = null;
		let edition = null;

		if (projectType) {
			application = getApplicationForProject(projectType);

			if (application && context && context.appEditions) {
				edition = getEdition(application, context.appEditions);
			}
		}

		const panels = contentPanels.map((panel: ForgePanel | ContentPanel) => {
			if (panel.type === FORGE_ENTITY_TYPE) {
				const { name, appKey, moduleKey, dateAdded, panelInstanceId, extension } =
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
					panel as any;

				const panelKey = toForgeKeyWithDate(appKey, moduleKey, panelInstanceId, dateAdded);
				return (
					<EcosystemIssueViewErrorBoundary
						application={application}
						edition={edition}
						experience="issueViewContentItemForgePanel"
						key={panel.name}
					>
						<ForgeContentPanel
							key={panelKey}
							panelKey={panelKey}
							name={name}
							appKey={appKey}
							moduleKey={moduleKey}
							extension={extension}
							isCompactMode={isCompactMode}
							externalId="issue.ecosystem.content-panel.forge"
						/>
					</EcosystemIssueViewErrorBoundary>
				);
			}

			if (
				ff('hrz-716-integrate-goal-picker-into-issue-view') &&
				panel.moduleKey.includes('linked-goals-content-panel')
			) {
				return null;
			}

			return (
				<EcosystemIssueViewErrorBoundary
					application={application}
					edition={edition}
					experience="issueViewContentItemConnectPanel"
					key={panel.name}
				>
					<ConnectContentPanel
						key={panel.name}
						name={panel.name}
						appKey={panel.appKey}
						moduleKey={panel.moduleKey}
					/>
				</EcosystemIssueViewErrorBoundary>
			);
		});

		return <>{panels}</>;
	}

	renderAdditionalWebLinksSections() {
		const { additionalWebLinksSections } = this.props;

		return (
			<>
				{additionalWebLinksSections.length > 0 && (
					<FireTrackAnalytics
						eventName="additionalWebLinksSections shown"
						componentName="additionalWebLinksSections"
					/>
				)}
				{additionalWebLinksSections.map(({ applicationId, applicationName }) => (
					<Fragment key={applicationId}>
						{this.maybeWrapInSectionWrapper(
							<WebLinksConnectedView
								applicationId={applicationId}
								applicationName={applicationName}
							/>,
						)}
					</Fragment>
				))}
			</>
		);
	}

	render() {
		const {
			isComplete,
			isCompactMode,
			issueKey,
			isEpic,
			isAnonymousUser,
			hasIssueLinksEntity,
			shouldRenderChildrenIssues,
			shouldRenderPortfolioChildrenIssues,
			shouldRenderSubtasks,
			isServiceDesk,
			issueViewRelayFragment,
		} = this.props;

		const issueLinks = hasIssueLinksEntity ? <IssueLinksView /> : null;

		const subtasks = shouldRenderSubtasks ? <SubtasksView key={issueKey} /> : null;
		const childrenIssues = shouldRenderChildrenIssues ? (
			<ChildrenIssuesView key={issueKey} />
		) : null;
		const portfolioChildIssues = shouldRenderPortfolioChildrenIssues ? (
			// @ts-expect-error TS274 missing analyticsSource, issueTypes, fetchIssueTypesStatus, onAddChildClick, and 3 more.
			<PortfolioChildIssuesView key={issueKey} />
		) : null;

		const epicIssues = isEpic ? <EpicIssuesView key={issueKey} /> : null;

		const attachments = isComplete ? <AttachmentsView /> : null;

		const devOpsAppRecommendation = isDevOpsAppRecommenderEnabled() ? (
			<Placeholder name="devOpsAppRecommendation" fallback={null}>
				<AsyncDevOpsAppRecommendationPanel />
			</Placeholder>
		) : null;

		const designPanel = !isAnonymousUser ? <DesignPanel /> : null;

		return (
			<UFOSegment name="issue-content-items">
				<ContentItemsWrapper isCompactMode={isCompactMode}>
					<DevOpsAppRecommendationContextProvider>
						<PerformanceMark metricKey={PerfMarks.DESCRIPTION_BEGIN_MARK_KEY} />
						<ContentTemplateRenderer issueViewRelayFragment={issueViewRelayFragment} />
						<PerformanceMark metricKey={PerfMarks.DESCRIPTION_END_MARK_KEY} />

						<PerformanceMark metricKey={PerfMarks.ATTACHMENTS_BEGIN_MARK_KEY} />
						{this.maybeWrapInSectionWrapper(attachments)}
						<PerformanceMark metricKey={PerfMarks.ATTACHMENTS_END_MARK_KEY} />

						<PerformanceMark metricKey={PerfMarks.DESIGNS_BEGIN_MARK_KEY} />
						{this.maybeWrapInSectionWrapper(designPanel)}
						<PerformanceMark metricKey={PerfMarks.DESIGNS_END_MARK_KEY} />

						<PerformanceMark metricKey={PerfMarks.DEV_OPS_APP_RECOMMENDATIONS_BEGIN_MARK_KEY} />
						{this.maybeWrapInSectionWrapper(devOpsAppRecommendation)}
						<PerformanceMark metricKey={PerfMarks.DEV_OPS_APP_RECOMMENDATIONS_END_MARK_KEY} />

						<PerformanceMark metricKey={PerfMarks.EPICS_BEGIN_MARK_KEY} />
						{this.maybeWrapInSectionWrapper(epicIssues)}
						<PerformanceMark metricKey={PerfMarks.EPICS_END_MARK_KEY} />

						<PerformanceMark metricKey={PerfMarks.CHILDREN_ISSUES_BEGIN_MARK_KEY} />
						{this.maybeWrapInSectionWrapper(childrenIssues)}
						<PerformanceMark metricKey={PerfMarks.CHILDREN_ISSUES_END_MARK_KEY} />

						<PerformanceMark metricKey={PerfMarks.PORTFOLIO_CHILD_ISSUES_BEGIN_MARK_KEY} />

						{this.maybeWrapInSectionWrapper(portfolioChildIssues)}

						<PerformanceMark metricKey={PerfMarks.PORTFOLIO_CHILD_ISSUES_END_MARK_KEY} />

						<PerformanceMark metricKey={PerfMarks.SUBTASKS_BEGIN_MARK_KEY} />

						{this.maybeWrapInSectionWrapper(subtasks)}

						<PerformanceMark metricKey={PerfMarks.SUBTASKS_END_MARK_KEY} />

						<PerformanceMark metricKey={PerfMarks.ISSUE_LINKS_BEGIN_MARK_KEY} />

						{this.maybeWrapInSectionWrapper(issueLinks)}

						<PerformanceMark metricKey={PerfMarks.ISSUE_LINKS_END_MARK_KEY} />

						{isServiceDesk && (
							<>
								<PerformanceMark metricKey={PerfMarks.INCIDENT_INVESTIGATIONS_BEGIN_MARK_KEY} />

								{this.maybeWrapInSectionWrapper(<IncidentInvestigationPanel />)}

								<PerformanceMark metricKey={PerfMarks.INCIDENT_INVESTIGATIONS_END_MARK_KEY} />
							</>
						)}

						<PerformanceMark metricKey={PerfMarks.OBJECT_RELATED_REQUESTS_BEGIN_MARK_KEY} />

						{this.maybeWrapInSectionWrapper(<ObjectRelatedRequestsPanel />)}

						<PerformanceMark metricKey={PerfMarks.OBJECT_RELATED_REQUESTS_END_MARK_KEY} />

						<PerformanceMark metricKey={PerfMarks.SMART_RELATED_ISSUES_BEGIN_MARK_KEY} />

						{this.maybeWrapInSectionWrapper(<SmartRelatedIssuesPanel />)}

						<PerformanceMark metricKey={PerfMarks.SMART_RELATED_ISSUES_END_MARK_KEY} />

						<PerformanceMark metricKey={PerfMarks.CONFLUENCE_PAGES_BEGIN_MARK_KEY} />

						{this.maybeWrapInSectionWrapper(<ConfluenceContentView />)}

						<PerformanceMark metricKey={PerfMarks.CONFLUENCE_PAGES_END_MARK_KEY} />

						<PerformanceMark metricKey={PerfMarks.WEB_LINKS_BEGIN_MARK_KEY} />

						{this.maybeWrapInSectionWrapper(<WebLinksConnectedView />)}

						<PerformanceMark metricKey={PerfMarks.WEB_LINKS_END_MARK_KEY} />

						<PerformanceMark metricKey={PerfMarks.ADDITIONAL_WEB_LINKS_BEGIN_MARK_KEY} />

						{this.renderAdditionalWebLinksSections()}

						<PerformanceMark metricKey={PerfMarks.ADDITIONAL_WEB_LINKS_END_MARK_KEY} />

						<PerformanceMark metricKey={PerfMarks.ECOSYSTEM_CONTENT_BEGIN_MARK_KEY} />

						{this.maybeWrapInSectionWrapper(
							ff('ecosystem_content_panel_lhs_6eptr')
								? this.renderEcosystemContentPanelsNew()
								: this.renderEcosystemContentPanels(),
						)}

						<PerformanceMark metricKey={PerfMarks.ECOSYSTEM_CONTENT_END_MARK_KEY} />

						<PerformanceMark metricKey={PerfMarks.LINKED_ALERTS_BEGIN_MARK_KEY} />

						{this.maybeWrapInSectionWrapper(<LinkedAlertsModals />)}

						<PerformanceMark metricKey={PerfMarks.LINKED_ALERTS_END_MARK_KEY} />

						<>
							<PerformanceMark metricKey={PerfMarks.PROFORMA_FORMS_BEGIN_MARK_KEY} />
							{this.maybeWrapInSectionWrapper(
								<WrappedProformaFormsPanelView issueKey={issueKey} />,
							)}
							<PerformanceMark metricKey={PerfMarks.PROFORMA_FORMS_END_MARK_KEY} />
						</>
					</DevOpsAppRecommendationContextProvider>
				</ContentItemsWrapper>
			</UFOSegment>
		);
	}
}

const ReduxConnectedView = connect(
	(state: State) => ({
		issueKey: issueKeySelector(state),
		contentPanels: ecosystemAddedContentPanelsSelector(state),
		isComplete: isCompletedLoadingSelector(state),
		isEpic: isEpicSelector(state),
		isAnonymousUser: isAnonymousSelector(state),
		hasIssueLinksEntity: isIssueLinksEnabledSelector(state),
		hasIssueLinksField: hasIssueLinksFieldSelector(state),
		shouldRenderChildrenIssues: shouldRenderChildPanelSelector(state),
		shouldRenderSubtasks: shouldRenderSubtaskPanelSelector(state),
		shouldRenderPortfolioChildrenIssues: shouldRenderPortfolioChildPanelSelector(state),
		additionalWebLinksSections: additionalWebLinksSectionsSelector(state),
		isServiceDesk: isServiceDeskSelector(state),
		...(ff('ecosystem_content_panel_lhs_6eptr')
			? {
					projectType: projectTypeSelector(state),
					context: contextSelector(state),
				}
			: {}),
	}),
	{},
)(withGICHook(ContentItems));

export default ReduxConnectedView;
